import React from 'react';
import { CareerFocusWithMetrics } from '../../hooks/useCareerFocus';
import { useDarkMode } from '../../context/DarkModeContext';
import { Temporal } from '@js-temporal/polyfill';
import { getInternshipDuration, getCurrentWeek } from '../../utils/internshipDuration';
import { getProgressDescription, getTimeRemainingContext } from '../../utils/timingContext';
import { getInternshipStatus, getAdaptiveInternshipDuration } from '../../utils/internshipStatus';
import { IoInfiniteOutline } from 'react-icons/io5';

interface TimelineProgressProps {
  focus: CareerFocusWithMetrics;
  timelineRef?: React.RefObject<HTMLDivElement>;
}

const TimelineProgress: React.FC<TimelineProgressProps> = ({ focus, timelineRef }) => {
  const { darkMode } = useDarkMode();

  // Calculate timeline progress
  const startDate = Temporal.PlainDate.from(focus.start_date);
  const endDate = focus.end_date ? Temporal.PlainDate.from(focus.end_date) : null;
  const today = Temporal.Now.plainDateISO();
  
  // Get status info
  const status = getInternshipStatus(focus);
  const isOpenEnded = status.metadata.isOpenEnded;
  
  // Use adaptive duration for open-ended internships
  const totalWeeks = isOpenEnded ? getAdaptiveInternshipDuration(focus) : getInternshipDuration(focus);
  const currentWeek = getCurrentWeek(focus);
  const progressPercentage = (currentWeek / totalWeeks) * 100;

  // Calculate days remaining and total days
  const daysRemaining = endDate ? endDate.since(today).days : null;
  const totalDays = endDate ? endDate.since(startDate).days : null;
  
  // Get contextual descriptions
  const progressDescription = getProgressDescription(progressPercentage);
  const timeRemainingDescription = daysRemaining !== null && totalDays !== null 
    ? getTimeRemainingContext(daysRemaining, totalDays)
    : isOpenEnded 
      ? 'Ongoing internship • No end date set' 
      : 'Timeline in progress';

  const bgColor = darkMode ? 'bg-gray-900' : 'bg-gray-100';
  const sectionBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';

  const formatDate = (date: Temporal.PlainDate) => {
    const jsDate = new Date(date.toString());
    return jsDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div ref={timelineRef} className={`${bgColor}`}>
      <div className="max-w-7xl mx-auto px-6 pt-4">
        <div className={`timeline-progress ${sectionBg} rounded-xl p-5 border ${borderColor}`} data-tour="timeline-progress">
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className={`text-lg font-semibold ${textColor} flex items-center gap-2`}>
                Week {currentWeek} of {totalWeeks}
                {isOpenEnded && (
                  <IoInfiniteOutline className="w-5 h-5 text-purple-500" title="Open-ended internship" />
                )}
              </div>
              <div className={`text-sm ${mutedTextColor}`}>
                {timeRemainingDescription}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-semibold text-blue-500">
                {Math.round(progressPercentage)}%
              </div>
              <div className={`text-sm ${mutedTextColor}`}>{progressDescription}</div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className={`h-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded overflow-hidden mb-2`}>
            <div 
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            />
          </div>
          
          {/* Timeline Dates */}
          <div className={`flex justify-between text-xs ${mutedTextColor}`}>
            <span>{formatDate(startDate)}</span>
            {endDate ? (
              <span>{formatDate(endDate)}</span>
            ) : (
              <button className="text-purple-500 hover:text-purple-600 font-medium">
                Set end date
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineProgress;